import { defineMutator } from '@rocicorp/zero';
import { getSettings } from '../queries.js';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { BSAFormula, CatalogType, DiabetesSchema, DoseCalcModeActive, Gender, GfrMethod, IBWFormula, PatientSource, PatientTherapySequenceStatus, SerumUnit, Sex, Title, VolumeMode, } from '../database/schema.js';
import { schema, zql } from '../schema.js';
import { getDosingWeight, isAibwMissing } from '../util/calculations/dosing-weight.js';
import { LOCAL_ID_SYSTEM } from '../util/constants.js';
import { getTotalAmountOfSubstanceDecimal } from '../util/calculations/patient-drug-total-amount.js';
import { loadSubstanceRoundingModes } from './patient/substance-rounding-modes.js';
import { titleCase, toSortableField } from '../util/index.js';
import { computePatientSearchText, getAgeInMonths } from '../util/patients.js';
import { inUseGfrQuery } from '../util/patient-gfr.js';
import { canAddPatientTherapy, CarrierSolutionValidationSchema, getPrescriptionRoles, getTherapySequenceInitialStatus, } from '../util/patients-therapies.js';
import { Address } from '../util/schemas.js';
import { assertCan, assertIsAuthenticated, EventLogs, isDefined } from '../utils.js';
import { assignPatientProtocol } from './patient/assign-patient-protocol.js';
import { removePatientProtocol } from './patient/remove-patient-protocol.js';
import { reactivatePatientProtocol } from './patient/reactivate-patient-protocol.js';
import { stopPatientProtocol } from './patient/stop-patient-protocol.js';
import { stopAllPatientTreatment } from './patient/stop-all-treatment.js';
import { updatePatientProtocolCycles } from './patient/update-patient-protocol-cycles.js';
import { computeTherapyDateFromCycleDay } from '../util/protocol-cycles.js';
import { GFR_PLAUSIBLE_MAX, isGfrPlausible } from '../util/calculations/gfr.js';
import { duplicateTherapySequences } from './patient/duplicate-therapy-sequences.js';
import { getDoseBasis } from './patient/dose-basis.js';
import { DoseLimitConfirmation } from './patient/dose-limit-log.js';
import { characteristicsLog, contactItems, personalDataItems } from './patient/patient-log-items.js';
import { medicineLogItems } from './patient/medicine-log-items.js';
import { itemsDetail, valueItem } from '../util/event-log-items.js';
import { Decimal } from 'decimal.js';
import { addFixedDateTherapyParameter, addProtocolTherapyParameter, logAmmsMfnChangeForSequence, } from './patient/add-therapy-parameter.js';
import { medicineUpdateTotalAmounts, updatePatientPrescriptionTherapy, updatePatientProtocolTherapy, } from './patient/medicine-update-total-amounts.js';
import { updateFixedDateTherapyParameter, updateProtocolTherapyParameter } from './patient/update-therapy-parameter.js';
import { addInstructionNote, updateInstructionNote } from './patient/patient-instruction-notes.js';
import { addEventLog, DeleteResourceSchema, EventLogResources } from './util.js';
import { createAddress, updateAddress } from './utils/address-utils.js';
import { MedicineSchema } from './utils/protocols.js';
function assertCanAddPatientTherapy(groups) {
    if (!canAddPatientTherapy(getPrescriptionRoles(groups))) {
        throw new Error('Only a doctor can add a prescription to a patient therapy.');
    }
}
export const AddPatientSchema = z.object({
    /**
     * The ID should come from the client so it is able to redirect the user to the patient's page after creation, because zero does not support returning data from mutations.
     */
    id: z.uuidv7(),
    title: Title.nullable().optional(),
    nationalId: z.string().optional(),
    givenName: z.string(),
    surname: z.string(),
    sex: Sex.optional(),
    gender: Gender.optional(),
    birthDate: z.number(),
    email: z.string().optional(),
    phone: z.string().nullable().optional(),
    businessPhone: z.string().nullable().optional(),
    address: Address.optional(),
    hospitalPatientId: z.string().optional(),
    barcodePrintFlag: z.boolean(),
    // insurance
    insuranceNumber: z.string().optional(),
    // encounter
    startedAt: z.number().optional(),
    endedAt: z.number().optional(),
    patientClass: z.string().optional(),
    admissionType: z.string().optional(),
    sectionId: z.uuidv7().optional(),
    wardId: z.uuidv7().optional(),
    familyDoctorId: z.uuidv7().optional(),
    responsibleDoctorId: z.uuidv7().optional(),
});
export const UpdatePatientSchema = z.object({
    id: z.uuidv7(),
    nationalId: z.string().optional(),
    sex: Sex.nullable().optional(),
    gender: Gender.nullable().optional(),
    title: Title.nullable().optional(),
    givenName: z.string().optional(),
    surname: z.string().optional(),
    birthDate: z.number().nullable().optional(),
    nationality: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    businessPhone: z.string().nullable().optional(),
    address: Address.optional(),
    notes: z.string().nullable().optional(),
    familyDoctorId: z.string().optional(),
    responsibleDoctorId: z.string().nullable().optional(),
    hospitalPatientId: z.string().optional(),
    barcodePrintFlag: z.boolean().optional(),
});
export const UpdateEmergencyContactSchema = z.object({
    id: z.uuidv7(),
    name: z.string(),
    phone: z.string(),
});
export const UpdateInsuranceSchema = z.object({
    patientId: z.uuidv7(),
    country: z.string().optional(),
    companyId: z.uuidv7().optional(),
    insuranceNumber: z.string().optional(),
    policyNumber: z.string().optional(),
    expirationDate: z.number().optional(),
    privatePatient: z.boolean().optional(),
});
export const AddPatientGfrSchema = z.object({
    patientId: z.uuidv7(),
    labTime: z.number(),
    urineCreatinine: z.number().optional(),
    gfr: z.number(),
    urineVolume: z.number().optional(),
    collectionTime: z.number().optional(),
    method: GfrMethod,
    serumCreatinine: z.number().optional(),
    serumUnit: SerumUnit.optional(),
});
export const AddCharacteristicsSchema = z.object({
    patientId: z.uuidv7(),
    height: z.number().optional(),
    weight: z.number().optional(),
    gfr: z.number().optional(),
    bsa: z.number().optional(),
    bsaFormula: BSAFormula.optional(),
    aibwFormula: IBWFormula.optional(),
    aibw: z.number().optional(),
    diabetes: DiabetesSchema.optional(),
    smoker: z.boolean().optional(),
    alcoholic: z.boolean().optional(),
    useAibw: z.boolean().optional(),
    diagnosisId: z.uuid().optional(),
});
export const UpdateEncounterSchema = z.object({
    patientId: z.uuidv7(),
    // Family doctor stored on the patient (general_physicians).
    familyDoctorId: z.uuid().nullable().optional(),
    sectionId: z.uuid().nullable().optional(),
    wardId: z.uuid().nullable().optional(),
    visitNumber: z.string().optional(),
    ambulatory: z.boolean().optional(),
    customerId: z.uuid().nullable().optional(),
    // Responsible doctor stored on the patient (a user).
    responsibleDoctorId: z.uuid().nullable().optional(),
});
export const AssignProtocolToPatientSchema = z.object({
    patientProtocolId: z.uuidv7(),
    patientId: z.uuidv7(),
    protocolId: z.uuidv7(),
    startDate: z.number(),
    cycles: z.int().min(1),
    startTime: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
    responsibleDoctorId: z.uuid().nullable().optional(),
    removedSequences: z.array(z.string()).optional(),
});
export const UpdatePatientProtocolCyclesSchema = z.object({
    patientProtocolId: z.uuidv7(),
    /**
     * The full, desired list of cycles after the edit (Edit protocol dialog), one entry per row
     * shown there - moved, renumbered and newly added cycles alike. A cycle not in
     * `deletedCycles` and missing here is left untouched.
     */
    cycles: z
        .array(z.object({
        /** The cycle's number after the edit, e.g. cycle 3 becomes cycle 2 once cycle 1 is deleted. */
        cycle: z.int().min(1),
        /**
         * The pre-edit cycle number this row carries over from - its prescriptions are moved
         * and/or renumbered to `cycle`/`startDate`. `null` means this is a brand-new cycle with
         * no existing prescriptions, so its prescriptions are generated from the protocol
         * template.
         */
        sourceCycle: z.int().min(1).nullable(),
        /** This cycle's start date after the edit, as a timestamp (ms). */
        startDate: z.number(),
    }))
        .min(1),
    /** Pre-edit cycle numbers removed entirely; their prescriptions are cancelled/deleted. */
    deletedCycles: z.array(z.int().min(1)),
    /** Free-text reason for the change, recorded in the event log. */
    reason: z.string().optional(),
});
export const RemovePatientProtocolSchema = z.object({
    patientProtocolId: z.uuidv7(),
    reason: z.string().optional(),
});
export const StopPatientProtocolSchema = z.object({
    patientProtocolId: z.uuidv7(),
    reason: z.string().optional(),
});
export const StopAllTreatmentSchema = z.object({
    patientId: z.uuidv7(),
    reason: z.string().optional(),
});
export const ReactivatePatientProtocolSchema = z.object({
    patientProtocolId: z.uuidv7(),
});
export const InstructionNoteSchema = z.object({
    patientId: z.uuidv7(),
    patientProtocolId: z.array(z.string()),
    fixedDate: z.number().nullable().optional(),
    cycle: z.number().optional(),
    day: z.number(),
    time: z.string(),
    isNote: z.boolean(),
    note: z.string(),
    recurringCycle: z.boolean().optional(),
});
export const UpdateInstructionNoteSchema = z.object({
    id: z.uuidv7(),
    patientId: z.uuidv7(),
    fixedDate: z.number().nullable().optional(),
    cycle: z.number().optional(),
    day: z.number().optional(),
    time: z.string().optional(),
    note: z.string().optional(),
    /** Protocol notes only: also apply the day/time/note to the notes of the later cycles it was added with. */
    applyToFutureNotes: z.boolean().optional(),
});
export const MedicineSchemaNew = z.object({
    id: z.string().optional(),
    activeSubstanceId: z.string(),
    prescribedNameId: z.string().optional(),
    autIdem: z.boolean().default(false),
    calculationMethod: DoseCalcModeActive,
    regularDose: z.number(),
    precisionDecimals: z.number(),
    doseProportion: z.number().min(0).max(100).optional(),
});
function validateCarrierSolutionOnSave(value, ctx) {
    const result = CarrierSolutionValidationSchema.safeParse({
        carrierSolutionId: value.carrierSolutionId,
        volume: value.volume,
        volumeMode: value.volumeMode,
    });
    if (result.success)
        return;
    for (const issue of result.error.issues) {
        ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message });
    }
}
const therapyParameterSchemaNewBase = z.object({
    patientId: z.uuidv7(),
    patientProtocolId: z.string().optional(),
    responsibleDoctorId: z.string().nullable().optional(),
    fixedDate: z.number().nullable().optional(),
    cycle: z.number().min(1).optional(),
    day: z.number().min(0).optional(),
    time: z.string().min(1),
    isNote: z.boolean().default(false),
    note: z.string().optional(),
    carrierSolutionId: z.string().optional(),
    applicationFormId: z.string(),
    duration: z.number().min(0),
    volume: z.number().min(0).optional(),
    volumeMode: VolumeMode.optional(),
    medicine: z.array(MedicineSchemaNew),
    therapyNote: z.string().optional(),
    autIdem: z.boolean().optional(),
    recurringCycle: z.boolean().optional(),
    pharmacyNote: z.string().optional(),
    /** soft dose limit warnings the prescriber proceeded past; logged, never used for the dose */
    doseLimitConfirmed: z.array(DoseLimitConfirmation).optional(),
});
export const TherapyParameterSchemaNew = therapyParameterSchemaNewBase.superRefine(validateCarrierSolutionOnSave);
// CY-280: the Duplicate dialog's copies, all in one transaction
export const DuplicateTherapySequencesSchema = z.object({
    patientId: z.uuidv7(),
    sources: z
        .array(z.object({
        therapySequenceId: z.uuidv7(),
        /** the copy's therapy date, start of day UTC */
        newDate: z.number(),
        /** dose per unit overrides; a medicine not listed keeps the source dose */
        medicines: z.array(z.object({ medicineId: z.uuidv7(), regularDose: z.number() })).default([]),
    }))
        .min(1),
    /** protocols that get one new, empty cycle starting on their first copied date */
    addCycleFor: z.array(z.uuidv7()).default([]),
});
export const TherapyParameterSchema = z.object({
    patientId: z.uuidv7(),
    patientProtocolId: z.array(z.string()),
    fixedDate: z.number().nullable().optional(),
    day: z.number().min(0),
    time: z.string().min(1),
    isNote: z.boolean().default(false),
    note: z.string().optional(),
    carrierSolutionId: z.array(z.string()),
    applicationFormId: z.array(z.string()),
    duration: z.number().min(0),
    volume: z.number().min(0),
    volumeMode: z.array(VolumeMode),
    medicine: z.array(MedicineSchema),
});
export const UpdateTherapyParameterSchema = z.object({
    id: z.uuidv7(),
    patientId: z.uuidv7(),
    patientProtocolId: z.array(z.string()),
    fixedDate: z.number().nullable().optional(),
    day: z.number().min(0),
    time: z.string().min(1),
    isNote: z.boolean().default(false),
    note: z.string().optional(),
    carrierSolutionId: z.array(z.string()),
    applicationFormId: z.array(z.string()),
    duration: z.number().min(0),
    volume: z.number().min(0),
    volumeMode: z.array(VolumeMode),
    medicine: z.array(MedicineSchema.extend({ id: z.uuidv7().optional() })),
});
export const UpdateTherapyParameterSchemaNew = therapyParameterSchemaNewBase
    .extend({
    id: z.uuidv7(),
    medicine: z.array(MedicineSchemaNew.extend({ id: z.string().optional() })),
    shiftFutureSequences: z.boolean().optional(),
})
    .superRefine(validateCarrierSolutionOnSave);
export const SavePatientDoseReductionSchema = z.object({
    patientId: z.uuidv7(),
    doseReduction: z.number().min(0).max(100).optional(),
    doseReductions: z
        .array(z.object({
        drugCatalogItemId: z.uuidv7(),
        doseReduction: z.number().min(0).max(100),
    }))
        .default([]),
});
export const ImportPatientFromHl7Schema = z.object({
    patientId: z.uuidv7(),
});
export const MergePatientsSchema = z.object({
    sourceId: z.uuidv7(),
    targetId: z.uuidv7(),
});
async function nextLocalPatientCode(tx, prefix = 'CY', base = 1000) {
    const prefixDash = `${prefix}-`;
    const existing = await tx.run(zql.patientIdentifiers.where('system', '=', LOCAL_ID_SYSTEM).where('value', 'ILIKE', `${prefixDash}%`));
    const maxNum = existing
        .map(r => Number.parseInt(String(r.value).slice(prefixDash.length), 10))
        .filter((n) => Number.isFinite(n))
        .reduce((acc, n) => Math.max(acc, n), base) || base;
    return `${prefixDash}${maxNum + 1}`;
}
function findById(tx, patientId) {
    return tx.run(zql.patients
        .where('id', patientId)
        .related('identifiers', q => q.where('system', LOCAL_ID_SYSTEM))
        .one());
}
function assertNotPendingHl7Import(patient) {
    if (patient?.source === PatientSource.enum.hl7_message) {
        throw new Error('Patient received via HL7 must be imported before it can be edited.');
    }
}
async function assertPatientImported(tx, patientId) {
    assertNotPendingHl7Import(await tx.run(zql.patients.where('id', patientId).one()));
}
export async function inputSequenceRequiresAdditionalApproval(tx, medicines) {
    const activeSubstances = await tx.run(zql.drugCatalogItems
        .where('catalogType', CatalogType.enum.ACTIVE_SUBSTANCE)
        .where('id', 'IN', medicines.map(m => m.activeSubstanceId).filter(Boolean))
        .related('activeSubstanceDetails'));
    const prescribedNameIds = [];
    medicines.forEach(m => m?.prescribedNameId && prescribedNameIds.push(m.prescribedNameId));
    const prescribedNames = await tx.run(zql.prescribedNames
        .where('id', 'IN', prescribedNameIds)
        .related('drugCatalogItem', item => item.related('activeSubstanceDetails')));
    const requiresAdditionalApproval = Boolean(activeSubstances.some(subst => subst.activeSubstanceDetails?.additionalApprovalRequired) ||
        prescribedNames.some(pn => pn.drugCatalogItem?.activeSubstanceDetails?.additionalApprovalRequired));
    return requiresAdditionalApproval;
}
export async function nextLocalPrescriptionCode(tx, prefix = 'PR', base = 1000) {
    const prefixDash = `${prefix}-`;
    const existing = await tx.run(zql.patientTherapySequence.where('internalId', 'ILIKE', `${prefixDash}%`));
    const maxNum = existing
        .map(r => Number.parseInt(String(r.internalId).slice(prefixDash.length), 10))
        .filter((n) => Number.isFinite(n))
        .reduce((acc, n) => Math.max(acc, n), base) || base;
    return {
        prefixDash,
        maxNum,
    };
}
const LOGGED_CHARACTERISTICS = [
    'weight',
    'height',
    'bsa',
    'aibw',
    'bsaFormula',
    'aibwFormula',
    'useAibw',
    'diabetes',
    'smoker',
    'alcoholic',
    'diagnosisId',
];
/** The characteristics fields recorded in the event log; missing values become null so an unset field is not logged as removed. */
function pickLoggedCharacteristics(characteristics) {
    return Object.fromEntries(LOGGED_CHARACTERISTICS.map(key => [key, characteristics[key] ?? null]));
}
/** Patch from the previous to the new characteristics, each change preceded by a `test` op holding the old value. */
function createCharacteristicsPatch(oldValues, newValues) {
    return createPatch(oldValues, newValues).flatMap(op => {
        const key = op.path.slice(1);
        if (op.op === 'add' || !(key in oldValues))
            return [op];
        return [{ op: 'test', path: op.path, value: oldValues[key] }, op];
    });
}
const LOGGED_GFR_FIELDS = [
    'gfr',
    'method',
    'labTime',
    'serumCreatinine',
    'serumUnit',
    'urineCreatinine',
    'urineVolume',
    'collectionTime',
];
/** The lab fields of a GFR recorded in the event log; unset ones are left out. */
function pickLoggedGfr(gfr) {
    return Object.fromEntries(LOGGED_GFR_FIELDS.filter(key => gfr[key] != null).map(key => [key, gfr[key]]));
}
function formatLoggedNumber(value) {
    return value === null || value === undefined ? null : String(value);
}
async function findDiagnosisName(tx, id) {
    if (!id)
        return null;
    const diagnosis = await tx.run(zql.diagnoses.where('id', '=', id).one());
    return diagnosis?.name ?? null;
}
export const patients = {
    add: defineMutator(AddPatientSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'patients.edit');
        const id = input.id;
        const now = Date.now();
        const givenName = input.givenName;
        const familyName = input.surname;
        const fullName = [givenName, familyName].filter(Boolean).join(' ');
        const newPatient = {
            id,
            nationalId: input.nationalId,
            title: input.title,
            givenName,
            familyName,
            sortableGivenName: toSortableField(givenName),
            sortableFamilyName: toSortableField(familyName),
            fullName,
            searchText: computePatientSearchText({
                givenName,
                familyName,
                hospitalPatientId: input.hospitalPatientId,
                nationalId: input.nationalId,
                insuranceNumber: input.insuranceNumber,
            }),
            sex: input.sex,
            gender: input.gender,
            birthDate: input.birthDate,
            createdAt: now,
            createdBy: ctx.user.id,
            hospitalPatientId: input.hospitalPatientId,
            barcodePrintFlag: input.barcodePrintFlag,
            ...(input.familyDoctorId && { familyDoctorId: input.familyDoctorId }),
            ...(input.responsibleDoctorId && { responsibleDoctorId: input.responsibleDoctorId }),
        };
        await tx.mutate.patients.insert(newPatient);
        const value = await nextLocalPatientCode(tx, 'CY', 1000);
        await tx.mutate.patientIdentifiers.insert({
            id: uuidv7(),
            patientId: id,
            system: LOCAL_ID_SYSTEM,
            value,
            use: 'official',
            createdAt: now,
            createdBy: ctx.user.id,
        });
        const hasContact = input.email || input.phone !== undefined || input.businessPhone !== undefined || input.address !== undefined;
        if (hasContact) {
            let addressId = undefined;
            if (input.address) {
                const address = await createAddress(tx, input.address, ctx.user.id);
                addressId = address.id;
            }
            await tx.mutate.patientContacts.insert({
                id: uuidv7(),
                patientId: id,
                contactType: 'home',
                addressId,
                phone: input.phone ?? undefined,
                businessPhone: input.businessPhone ?? undefined,
                email: input.email ?? undefined,
                createdAt: now,
                createdBy: ctx.user.id,
            });
        }
        if (input.insuranceNumber) {
            await tx.mutate.patientInsurances.insert({
                id: uuidv7(),
                patientId: id,
                insuranceNumber: input.insuranceNumber,
                createdAt: now,
                createdBy: ctx.user.id,
            });
        }
        if (tx.location === 'server') {
            const patches = createPatch({}, newPatient);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'patient',
                resourceId: newPatient.id,
                action: 'create',
                domain: 'patient',
                event: EventLogs.PATIENT_CREATED,
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    code: value,
                    name: newPatient.fullName,
                },
            }, ctx);
        }
    }),
    updateDetails: defineMutator(UpdatePatientSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'patients.edit');
        const prev = await findById(tx, input.id);
        if (!prev)
            throw new Error('Not Found');
        assertNotPendingHl7Import(prev);
        const { id } = input;
        const now = Date.now();
        const givenName = input.givenName ?? '';
        const familyName = input.surname ?? '';
        const fullName = input.givenName || input.surname ? [givenName, familyName].filter(Boolean).join(' ') : prev.fullName;
        const needsSearchTextUpdate = input.givenName || input.surname || input.hospitalPatientId || input.nationalId;
        let searchText = prev.searchText;
        if (needsSearchTextUpdate) {
            const prevInsurance = await tx.run(zql.patientInsurances.where('patientId', '=', id).one());
            searchText = computePatientSearchText({
                givenName: input.givenName ? givenName : prev.givenName,
                familyName: input.surname ? familyName : prev.familyName,
                hospitalPatientId: input.hospitalPatientId ?? prev.hospitalPatientId,
                nationalId: input.nationalId ?? prev.nationalId,
                insuranceNumber: prevInsurance?.insuranceNumber,
            });
        }
        const patientUpdate = {
            id,
            nationalId: input.nationalId ?? undefined,
            title: input.title ?? undefined,
            givenName: input.givenName ? givenName : undefined,
            familyName: input.surname ? familyName : undefined,
            sortableGivenName: input.givenName ? toSortableField(givenName) : undefined,
            sortableFamilyName: input.surname ? toSortableField(familyName) : undefined,
            birthDate: input.birthDate ?? undefined,
            fullName,
            searchText,
            sex: input.sex ?? undefined,
            gender: input.gender ?? undefined,
            nationality: input.nationality ?? undefined,
            notes: input.notes ?? undefined,
            familyDoctorId: input.familyDoctorId ?? undefined,
            responsibleDoctorId: input.responsibleDoctorId ?? undefined,
            hospitalPatientId: input.hospitalPatientId ?? undefined,
            barcodePrintFlag: input.barcodePrintFlag ?? undefined,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.patients.update(patientUpdate);
        if (tx.location === 'server') {
            // fields left undefined are not part of this update
            const sent = Object.fromEntries(Object.entries(patientUpdate).filter(([, value]) => value !== undefined));
            const items = personalDataItems(prev, sent);
            if (items.length > 0) {
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'patient',
                    resourceId: id,
                    domain: 'patient',
                    event: EventLogs.PATIENT_UPDATED,
                    action: 'update',
                    patches: createPatch(prev, { ...prev, ...sent }),
                    performedBy: ctx.user.id,
                    performedAt: now,
                    status: 'OK',
                    details: { code: prev.identifiers[0]?.value, name: fullName, ...itemsDetail(items) },
                }, ctx);
            }
        }
        const hasContact = input.email || input.phone !== undefined || input.address !== undefined;
        if (!hasContact)
            return;
        const existing = await tx.run(zql.patientContacts
            .where('patientId', '=', id)
            .where('contactType', '=', 'home')
            .orderBy('createdAt', 'desc')
            .limit(1)
            .one());
        let addressId = undefined;
        if (input.address && existing?.addressId) {
            const address = await updateAddress(tx, existing.addressId, input.address, ctx.user.id);
            addressId = address.id;
        }
        if (input.address && !existing?.addressId) {
            const address = await createAddress(tx, input.address, ctx.user.id);
            addressId = address.id;
        }
        const updatedContact = {
            id: existing?.id || uuidv7(),
            patientId: id,
            contactType: 'home',
            phone: input.phone ?? undefined,
            businessPhone: input.businessPhone ?? undefined,
            addressId,
            email: input.email ?? undefined,
            ...(existing?.id ? { updatedAt: now, updatedBy: ctx.user.id } : { createdAt: now, createdBy: ctx.user.id }),
        };
        await tx.mutate.patientContacts.upsert(updatedContact);
        if (tx.location === 'server') {
            const patches = createPatch(existing || {}, {
                ...existing,
                ...updatedContact,
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'patient_contact_home',
                resourceId: id,
                domain: 'patient',
                event: EventLogs.PATIENT_UPDATED,
                action: 'update',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    code: prev.identifiers[0]?.value,
                    name: input.givenName || input.surname ? [givenName, familyName].filter(Boolean).join(' ') : prev.fullName,
                    ...itemsDetail(contactItems(existing, updatedContact)),
                },
            }, ctx);
        }
    }),
    updateEmergencyContact: defineMutator(UpdateEmergencyContactSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'patients.edit');
        const prev = await findById(tx, input.id);
        if (!prev)
            throw new Error('Not Found');
        assertNotPendingHl7Import(prev);
        const { id, name, phone } = input;
        const now = Date.now();
        const existing = await tx.run(zql.patientContacts
            .where('patientId', '=', id)
            .where('contactType', '=', 'emergency')
            .orderBy('createdAt', 'desc')
            .limit(1)
            .one());
        const updatedContact = {
            id: existing?.id || uuidv7(),
            patientId: id,
            contactType: 'emergency',
            name: titleCase(name),
            phone,
            ...(existing?.id ? { updatedAt: now, updatedBy: ctx.user.id } : { createdAt: now, createdBy: ctx.user.id }),
        };
        await tx.mutate.patientContacts.upsert(updatedContact);
        if (tx.location === 'server') {
            const patches = createPatch(existing || {}, {
                ...existing,
                ...updatedContact,
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'patient_contact_emergency',
                resourceId: id,
                action: 'update',
                domain: 'patient',
                event: EventLogs.PATIENT_UPDATED,
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    code: prev.identifiers[0]?.value,
                    name: prev.fullName,
                    ...itemsDetail(contactItems(existing, updatedContact)),
                },
            }, ctx);
        }
    }),
    updateInsurance: defineMutator(UpdateInsuranceSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'patients.edit');
        const prev = await findById(tx, input.patientId);
        if (!prev)
            throw new Error('Not Found');
        assertNotPendingHl7Import(prev);
        const { patientId, country, companyId, insuranceNumber, policyNumber, expirationDate, privatePatient } = input;
        const now = Date.now();
        const existing = await tx.run(zql.patientInsurances.where('patientId', '=', patientId).one());
        const updatedInsurance = {
            id: existing?.id || uuidv7(),
            patientId,
            country,
            companyId,
            insuranceNumber,
            policyNumber,
            expirationDate,
            privatePatient,
            ...(existing?.id ? { updatedAt: now, updatedBy: ctx.user.id } : { createdAt: now, createdBy: ctx.user.id }),
        };
        await tx.mutate.patientInsurances.upsert(updatedInsurance);
        const searchText = computePatientSearchText({
            givenName: prev.givenName,
            familyName: prev.familyName,
            hospitalPatientId: prev.hospitalPatientId,
            nationalId: prev.nationalId,
            insuranceNumber: input.insuranceNumber,
        });
        await tx.mutate.patients.update({ id: patientId, searchText });
        if (tx.location === 'server') {
            const patches = createPatch(existing || {}, {
                ...existing,
                ...updatedInsurance,
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'patient_insurance',
                domain: 'patient',
                event: EventLogs.PATIENT_UPDATED,
                resourceId: patientId,
                action: 'update',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    code: prev.identifiers[0]?.value,
                    name: prev.fullName,
                },
            }, ctx);
        }
    }),
    addCharacteristics: defineMutator(AddCharacteristicsSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'patients.edit');
        // weight-based doses would have no weight to use (see getDosingWeight)
        if (isAibwMissing(input))
            throw new Error('An AIBW is required when the AIBW is used for dosing');
        const prev = await findById(tx, input.patientId);
        if (!prev)
            throw new Error('Not Found');
        const prevCharacteristics = await tx.run(zql.patientCharacteristics
            .where('patientId', '=', input.patientId)
            .where('deletedAt', 'IS', null)
            .orderBy('createdAt', 'desc')
            .limit(1)
            .one());
        const { patientId, height, weight, gfr, diabetes, smoker, alcoholic, useAibw, diagnosisId, aibw, bsa, bsaFormula, aibwFormula, } = input;
        const now = Date.now();
        const characteristics = {
            id: uuidv7(),
            patientId,
            height,
            weight,
            gfr,
            bsa,
            aibw,
            createdAt: now,
            createdBy: ctx.user.id,
            diabetes,
            smoker,
            alcoholic,
            useAibw,
            diagnosisId,
            bsaFormula,
            aibwFormula,
        };
        await tx.mutate.patientCharacteristics.insert(characteristics);
        await medicineUpdateTotalAmounts(tx, patientId, [], {
            cause: 'characteristics',
            userId: ctx.user.id,
            impersonatedBy: ctx.impersonatedBy,
        });
        if (tx.location === 'server') {
            const oldValues = prevCharacteristics ? pickLoggedCharacteristics(prevCharacteristics) : {};
            const newValues = pickLoggedCharacteristics(characteristics);
            const patches = createCharacteristicsPatch(oldValues, newValues);
            if (patches.length === 0)
                return;
            const oldDiagnosisId = prevCharacteristics?.diagnosisId ?? null;
            const newDiagnosisId = characteristics.diagnosisId ?? null;
            const diagnosis = oldDiagnosisId === newDiagnosisId
                ? undefined
                : { old: await findDiagnosisName(tx, oldDiagnosisId), new: await findDiagnosisName(tx, newDiagnosisId) };
            const settings = await getSettings(tx);
            const { items, variant } = characteristicsLog(prevCharacteristics, characteristics, diagnosis, {
                bsaFormula: settings?.bsaFormula,
                aibwFormula: settings?.ibwFormula,
            });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientCharacteristics,
                domain: 'patient',
                event: EventLogs.PATIENT_CHARACTERISTICS_UPDATED,
                resourceId: patientId,
                action: prevCharacteristics ? 'update' : 'create',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    code: prev.identifiers[0]?.value,
                    name: prev.fullName,
                    oldWeight: formatLoggedNumber(prevCharacteristics?.weight),
                    newWeight: formatLoggedNumber(characteristics.weight),
                    oldHeight: formatLoggedNumber(prevCharacteristics?.height),
                    newHeight: formatLoggedNumber(characteristics.height),
                    variant,
                    ...itemsDetail(items),
                },
            }, ctx);
        }
    }),
    updateEncounter: defineMutator(UpdateEncounterSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'patients.edit');
        const prev = await findById(tx, input.patientId);
        if (!prev)
            throw new Error('Not Found');
        assertNotPendingHl7Import(prev);
        const { patientId, familyDoctorId, sectionId, wardId, ambulatory, customerId, responsibleDoctorId } = input;
        const now = Date.now();
        const existing = await tx.run(zql.patientEncounters.where('patientId', '=', patientId).one());
        const existingPatient = await findById(tx, patientId);
        if (!existingPatient)
            throw new Error('Not Found');
        const updatedEncounter = {
            id: existing?.id || uuidv7(),
            patientId,
            sectionId,
            wardId,
            ambulatory,
            customerId,
            visitNumber: existing?.visitNumber || '',
            ...(existing?.id ? { updatedAt: now, updatedBy: ctx.user.id } : { createdAt: now, createdBy: ctx.user.id }),
        };
        await tx.mutate.patientEncounters.upsert(updatedEncounter);
        if (familyDoctorId !== undefined || responsibleDoctorId !== undefined)
            await tx.mutate.patients.update({
                id: patientId,
                ...(familyDoctorId !== undefined && { familyDoctorId }),
                ...(responsibleDoctorId !== undefined && { responsibleDoctorId }),
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
        if (tx.location === 'server') {
            const patches = createPatch(existing || {}, {
                ...existing,
                ...updatedEncounter,
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'patient_encounter',
                domain: 'patient',
                event: EventLogs.PATIENT_UPDATED,
                resourceId: patientId,
                action: 'update',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    code: prev.identifiers[0]?.value,
                    name: prev.fullName,
                },
            }, ctx);
        }
    }),
    assignProtocol: defineMutator(AssignProtocolToPatientSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        assertCanAddPatientTherapy(ctx.groups);
        await assertPatientImported(tx, input.patientId);
        await assignPatientProtocol(tx, input, ctx.user.id);
    }),
    // CY-280: the Duplicate dialog's copies, all in one transaction; a pharmacist may copy too (copies wait for the doctor)
    duplicateTherapySequences: defineMutator(DuplicateTherapySequencesSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        assertCanAddPatientTherapy(ctx.groups);
        await assertPatientImported(tx, input.patientId);
        await duplicateTherapySequences(tx, input, ctx);
    }),
    updateProtocolCycles: defineMutator(UpdatePatientProtocolCyclesSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        const patientProtocol = await tx.run(zql.patientProtocols.where('id', input.patientProtocolId).one());
        if (patientProtocol)
            await assertPatientImported(tx, patientProtocol.patientId);
        await updatePatientProtocolCycles(tx, input, ctx.user.id);
    }),
    removeProtocol: defineMutator(RemovePatientProtocolSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        await removePatientProtocol(tx, input, ctx.user.id);
    }),
    stopProtocol: defineMutator(StopPatientProtocolSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        await stopPatientProtocol(tx, input, ctx.user.id);
    }),
    stopAllTreatment: defineMutator(StopAllTreatmentSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        await stopAllPatientTreatment(tx, input, ctx.user.id);
    }),
    reactivateProtocol: defineMutator(ReactivatePatientProtocolSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        await reactivatePatientProtocol(tx, input, ctx.user.id);
    }),
    addInstructionNote: defineMutator(InstructionNoteSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        await addInstructionNote(tx, input, ctx.user.id);
    }),
    updateInstructionNote: defineMutator(UpdateInstructionNoteSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        await updateInstructionNote(tx, input, ctx.user.id);
    }),
    addTherapyParameterNew: defineMutator(TherapyParameterSchemaNew, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        assertCanAddPatientTherapy(ctx.groups);
        await assertPatientImported(tx, input.patientId);
        if (!input.patientProtocolId) {
            return addFixedDateTherapyParameter(tx, input, ctx);
        }
        return addProtocolTherapyParameter(tx, input, ctx);
    }),
    addTherapyParameter: defineMutator(TherapyParameterSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        assertCanAddPatientTherapy(ctx.groups);
        await assertPatientImported(tx, input.patientId);
        const now = Date.now();
        const characteristics = await tx.run(zql.patientCharacteristics
            .where('patientId', '=', input.patientId)
            .related('patient')
            .orderBy('createdAt', 'desc')
            .limit(1)
            .one());
        const patient = await tx.run(zql.patients.where('id', input.patientId).one());
        const gfrLab = await tx.run(inUseGfrQuery(input.patientId));
        const roundingModes = await loadSubstanceRoundingModes(tx, input.medicine.map(med => med.activeSubstanceId[0]));
        if (input.patientProtocolId.length === 0) {
            if (!input.fixedDate) {
                throw new Error('Either patientProtocolId or fixedDate must be provided');
            }
            const requiresAdditionalApproval = false; // await inputSequenceRequiresAdditionalApproval(tx, input.medicine)
            const therapyDate = input.fixedDate;
            const prescriptionDate = new Date(therapyDate);
            const newTherapySequenceId = uuidv7();
            const internalIdStructure = await nextLocalPrescriptionCode(tx, `PR-${prescriptionDate.getFullYear()}${String(prescriptionDate.getMonth() + 1).padStart(2, '0')}${String(prescriptionDate.getDate()).padStart(2, '0')}`);
            const internalId = `${internalIdStructure.prefixDash}${internalIdStructure.maxNum + 1}`;
            const newPatientTherapySequence = {
                id: newTherapySequenceId,
                patientId: input.patientId,
                internalId,
                therapyDate,
                prescriptionSource: 'cypro',
                restVialCreated: false,
                status: getTherapySequenceInitialStatus(requiresAdditionalApproval),
                isNote: false,
                time: input.time,
                carrierSolutionId: input.carrierSolutionId[0] ?? undefined,
                applicationFormId: input.applicationFormId[0] ?? undefined,
                duration: input.duration ?? undefined,
                volume: input.volume ?? undefined,
                volumeMode: input.volumeMode[0] ?? undefined,
                ...getDoseBasis(characteristics, gfrLab),
                createdAt: now,
                createdBy: ctx.user.id,
            };
            await tx.mutate.patientTherapySequence.insert(newPatientTherapySequence);
            if (tx.location === 'server') {
                const patches = createPatch({}, newPatientTherapySequence);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.patientTherapySequence,
                    resourceId: newTherapySequenceId,
                    action: 'create',
                    domain: 'prescription',
                    event: EventLogs.PATIENT_THERAPY_SEQUENCE_ADDED,
                    patches,
                    details: {
                        id: internalId,
                        patientName: patient?.fullName,
                        patientId: input.patientId,
                        therapyDate: therapyDate?.toString(),
                        time: newPatientTherapySequence.time,
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
            const sequenceOps = [];
            const logSequenceOps = [];
            for (const med of input.medicine) {
                const totalAmount = getTotalAmountOfSubstanceDecimal({
                    calculationMethod: med.calculationMethod[0] || 'ABSOLUTE',
                    dose: med.regularDose,
                    doseProportion: med.doseProportion || 100,
                    roundingMode: med.activeSubstanceId[0] ? roundingModes.get(med.activeSubstanceId[0]) : undefined,
                    dosePrecision: med.precisionDecimals,
                    height: characteristics?.height ?? null,
                    weight: getDosingWeight(characteristics),
                    gfr: gfrLab?.gfr ?? null,
                    bsa: characteristics?.bsa ?? null,
                });
                const medicineToInsert = {
                    id: uuidv7(),
                    therapySequenceId: newTherapySequenceId,
                    patientId: input.patientId,
                    therapyDate,
                    activeSubstanceId: med.activeSubstanceId[0] ?? undefined,
                    prescribedNameId: med.prescribedNameId[0] ?? undefined,
                    autIdem: med.prescribedNameId[0] ? (med.autIdem ?? false) : false,
                    regularDose: med.regularDose,
                    precisionDecimals: med.precisionDecimals,
                    doseProportion: med.doseProportion ?? undefined,
                    totalAmount,
                    calculationMethod: med.calculationMethod[0] ?? undefined,
                    notes: med.notes ?? null,
                    ageRanges: med.ageRanges ?? undefined,
                    createdAt: now,
                    createdBy: ctx.user.id,
                };
                sequenceOps.push(() => tx.mutate.patientTherapySequenceMedicine.insert(medicineToInsert));
                if (tx.location === 'server') {
                    const patches = createPatch({}, medicineToInsert);
                    logSequenceOps.push(async () => addEventLog(tx, {
                        id: uuidv7(),
                        resource: EventLogResources.patientTherapySequence,
                        resourceId: medicineToInsert.id,
                        action: 'create',
                        domain: 'prescription',
                        event: EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_ADDED,
                        patches,
                        performedBy: ctx.user.id,
                        performedAt: now,
                        details: {
                            id: medicineToInsert.id,
                            patientName: patient?.fullName ?? '',
                            patientId: patient?.id ?? '',
                            therapyInternalId: newPatientTherapySequence.internalId,
                            ...itemsDetail(await medicineLogItems(tx, undefined, medicineToInsert)),
                        },
                    }));
                }
            }
            await Promise.all(sequenceOps.map(op => op()));
            await Promise.all(logSequenceOps.map(op => op()));
            return;
        }
        if (!Number.isFinite(input.day) || input.day < 0) {
            throw new Error('Day must be provided when patientProtocolId is provided');
        }
        const patientProtocolId = input.patientProtocolId[0];
        if (!patientProtocolId) {
            throw new Error('patientProtocolId is required');
        }
        const patientProtocol = await tx.run(zql.patientProtocols
            .related('protocol')
            .where('id', '=', patientProtocolId)
            .where('patientId', '=', input.patientId)
            .one());
        if (!patientProtocol || !patientProtocol.protocol) {
            throw new Error(`Patient Protocol not found: ${patientProtocolId}`);
        }
        const { totalCycles, daysRepetition } = patientProtocol.protocol;
        const cycleIndices = Array.from({ length: totalCycles }, (_, i) => i);
        const settings = await getSettings(tx);
        const isZeroDayProtocol = settings?.dayZeroProtocol;
        await Promise.all(cycleIndices.map(async (cycleIndex) => {
            const therapyDate = computeTherapyDateFromCycleDay(patientProtocol, cycleIndex + 1, input.day, daysRepetition, isZeroDayProtocol);
            if (therapyDate < now)
                return;
            const newTherapySequenceId = uuidv7();
            const requiresAdditionalApproval = false; //await inputSequenceRequiresAdditionalApproval(tx, input.medicine)
            const prescriptionDate = new Date(therapyDate);
            const internalIdStructure = await nextLocalPrescriptionCode(tx, `PR-${prescriptionDate.getFullYear()}${String(prescriptionDate.getMonth() + 1).padStart(2, '0')}${String(prescriptionDate.getDate()).padStart(2, '0')}`);
            const internalId = `${internalIdStructure.prefixDash}${internalIdStructure.maxNum + 1}`;
            const newPatientTherapySequence = {
                id: newTherapySequenceId,
                patientId: input.patientId,
                protocolId: patientProtocol.protocolId,
                patientProtocolId: patientProtocol.id,
                internalId,
                therapyDate,
                prescriptionSource: 'cypro',
                restVialCreated: false,
                day: input.day,
                cycle: cycleIndex + 1,
                status: getTherapySequenceInitialStatus(requiresAdditionalApproval),
                isNote: false,
                time: input.time,
                carrierSolutionId: input.carrierSolutionId[0] ?? undefined,
                applicationFormId: input.applicationFormId[0] ?? undefined,
                duration: input.duration ?? undefined,
                volume: input.volume ?? undefined,
                volumeMode: input.volumeMode[0] ?? undefined,
                ...getDoseBasis(characteristics, gfrLab),
                createdAt: now,
                createdBy: ctx.user.id,
            };
            await tx.mutate.patientTherapySequence.insert(newPatientTherapySequence);
            if (tx.location === 'server') {
                const patches = createPatch({}, newPatientTherapySequence);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.patientTherapySequence,
                    resourceId: newTherapySequenceId,
                    action: 'create',
                    domain: 'prescription',
                    event: EventLogs.PATIENT_THERAPY_SEQUENCE_PROTOCOL_ADDED,
                    patches,
                    details: {
                        id: internalId,
                        patientName: characteristics?.patient?.fullName ?? '',
                        patientId: input.patientId,
                        protocolName: patientProtocol?.protocol?.name ?? '',
                        cycle: newPatientTherapySequence?.cycle?.toString() ?? '',
                        day: newPatientTherapySequence?.day?.toString() ?? '',
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx);
            }
            await Promise.all(input.medicine.map(async (med) => {
                const totalAmount = getTotalAmountOfSubstanceDecimal({
                    calculationMethod: med.calculationMethod[0] || 'ABSOLUTE',
                    dose: med.regularDose,
                    doseProportion: med.doseProportion || 100,
                    roundingMode: med.activeSubstanceId[0] ? roundingModes.get(med.activeSubstanceId[0]) : undefined,
                    dosePrecision: med.precisionDecimals,
                    height: characteristics?.height ?? null,
                    weight: getDosingWeight(characteristics),
                    gfr: gfrLab?.gfr ?? null,
                    bsa: characteristics?.bsa ?? null,
                });
                const medicineToInsert = {
                    id: uuidv7(),
                    therapySequenceId: newTherapySequenceId,
                    patientId: input.patientId,
                    therapyDate,
                    activeSubstanceId: med.activeSubstanceId[0] ?? undefined,
                    prescribedNameId: med.prescribedNameId[0] ?? undefined,
                    autIdem: med.prescribedNameId[0] ? (med.autIdem ?? false) : false,
                    calculationMethod: med.calculationMethod[0] ?? undefined,
                    regularDose: med.regularDose,
                    totalAmount,
                    precisionDecimals: med.precisionDecimals,
                    notes: med.notes ?? null,
                    ageRanges: med.ageRanges ?? undefined,
                    createdAt: now,
                    createdBy: ctx.user.id,
                };
                if (tx.location === 'server') {
                    const patches = createPatch({}, medicineToInsert);
                    await addEventLog(tx, {
                        id: uuidv7(),
                        resource: EventLogResources.patientTherapySequence,
                        resourceId: medicineToInsert.id,
                        action: 'create',
                        domain: 'prescription',
                        event: EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_PROTOCOL_ADDED,
                        patches,
                        performedBy: ctx.user.id,
                        performedAt: now,
                        details: {
                            therapyInternalId: newPatientTherapySequence?.internalId ?? '',
                            patientName: patient?.fullName ?? '',
                            patientId: patient?.id ?? '',
                            ...itemsDetail(await medicineLogItems(tx, undefined, medicineToInsert)),
                        },
                    });
                }
                return tx.mutate.patientTherapySequenceMedicine.insert(medicineToInsert);
            }));
        }));
    }),
    updateTherapySequence: defineMutator(UpdateTherapyParameterSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        await assertPatientImported(tx, input.patientId);
        if (input.patientProtocolId.length === 0) {
            await updatePatientPrescriptionTherapy(tx, input, ctx.user.id);
            return;
        }
        await updatePatientProtocolTherapy(tx, input, ctx.user.id);
    }),
    updateTherapySequenceNew: defineMutator(UpdateTherapyParameterSchemaNew, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        await assertPatientImported(tx, input.patientId);
        const roles = getPrescriptionRoles(ctx.groups);
        if (!input.patientProtocolId) {
            return updateFixedDateTherapyParameter(tx, input, ctx.user.id, roles);
        }
        return updateProtocolTherapyParameter(tx, input, ctx.user.id, roles);
    }),
    deleteTherapySequence: defineMutator(z.object({ id: z.uuidv7() }), async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        const existing = await tx.run(zql.patientTherapySequence.where('id', '=', input.id).one());
        if (!existing)
            throw new Error('Therapy sequence not found');
        const patient = await findById(tx, existing.patientId);
        const nonDeletableStatuses = [
            PatientTherapySequenceStatus.enum.in_production,
            PatientTherapySequenceStatus.enum.administered,
        ];
        if (existing.status && nonDeletableStatuses.includes(existing.status)) {
            throw new Error('Cannot delete a therapy sequence that is in production or administered');
        }
        const now = Date.now();
        // Before the medicines are soft-deleted, so the MFN MDL still carries their ZDRs.
        await logAmmsMfnChangeForSequence(tx, { sequenceId: input.id, change: 'delete', now });
        await tx.mutate.patientTherapySequence.update({
            id: input.id,
            deletedAt: now,
            deletedBy: ctx.user.id,
        });
        if (tx.location === 'server') {
            const patches = createPatch(existing, { ...existing, deletedAt: now, deletedBy: ctx.user.id });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequence,
                resourceId: input.id,
                action: 'delete',
                domain: 'prescription',
                event: EventLogs.PATIENT_THERAPY_SEQUENCE_DELETED,
                patches,
                details: {
                    id: existing?.internalId,
                    patientName: patient?.fullName,
                    patientId: patient?.id,
                    ...itemsDetail(valueItem('date', existing.therapyDate, { type: 'date' })),
                },
                performedBy: ctx.user.id,
                performedAt: now,
            }, ctx);
        }
        if (existing.patientProtocolId) {
            const siblingTherapies = await tx.run(zql.patientTherapySequence
                .where('patientProtocolId', '=', existing.patientProtocolId)
                .where('id', '!=', existing.id)
                .where('deletedAt', 'IS', null));
            if (siblingTherapies.length <= 0) {
                await tx.mutate.patientProtocols.update({
                    id: existing.patientProtocolId,
                    deletedAt: now,
                    deletedBy: ctx.user.id,
                });
            }
        }
        const medicines = await tx.run(zql.patientTherapySequenceMedicine.where('therapySequenceId', '=', input.id).where('deletedAt', 'IS', null));
        const sequenceOps = [];
        const logSequenceOps = [];
        for (const med of medicines) {
            sequenceOps.push(() => tx.mutate.patientTherapySequenceMedicine.update({
                id: med.id,
                deletedAt: now,
                deletedBy: ctx.user.id,
            }));
            if (tx.location === 'server') {
                const patches = createPatch(med, { ...med, deletedAt: now, deletedBy: ctx.user.id });
                logSequenceOps.push(async () => addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.patientTherapySequence,
                    resourceId: input.id,
                    action: 'delete',
                    domain: 'prescription',
                    event: EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_DELETED,
                    patches,
                    details: {
                        id: med?.id,
                        patientName: patient?.fullName ?? '',
                        patientId: patient?.id ?? '',
                        therapyInternalId: existing?.internalId ?? '',
                        ...itemsDetail(await medicineLogItems(tx, med, undefined)),
                    },
                    performedBy: ctx.user.id,
                    performedAt: now,
                }, ctx));
            }
        }
        await Promise.all(sequenceOps.map(op => op()));
        await Promise.all(logSequenceOps.map(op => op()));
    }),
    saveDoseReduction: defineMutator(SavePatientDoseReductionSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'prescriptions.edit');
        const { patientId, doseReductions, doseReduction } = input;
        await assertPatientImported(tx, patientId);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const now = today.getTime();
        if (!doseReductions.length && doseReduction === undefined)
            return;
        const isGlobalReduction = doseReduction !== undefined;
        const reductionDecimal = doseReduction ? new Decimal(doseReduction) : new Decimal(0);
        const reducedBy = new Decimal(100).minus(reductionDecimal).toNumber();
        if (isGlobalReduction) {
            const medicines = await tx.run(zql.patientTherapySequenceMedicine
                .where('patientId', '=', patientId)
                .where('therapyDate', '>=', now)
                .whereExists('therapySequence', sequence => sequence.where('status', 'IN', [
                PatientTherapySequenceStatus.enum.pending_doctor_validation,
                PatientTherapySequenceStatus.enum.pending_approval,
            ]))
                .where('deletedAt', 'IS', null));
            await Promise.all(medicines.map(async (med) => {
                if (!med.regularDose) {
                    return;
                }
                await tx.mutate.patientTherapySequenceMedicine.update({
                    id: med.id,
                    doseProportion: reducedBy,
                    updatedAt: now,
                    updatedBy: ctx.user.id,
                });
            }));
            await medicineUpdateTotalAmounts(tx, patientId);
            return;
        }
        const drugItemIds = doseReductions.map(dr => dr.drugCatalogItemId);
        const medicines = await tx.run(zql.patientTherapySequenceMedicine
            .where('patientId', '=', patientId)
            .where(({ cmp, or }) => or(cmp('activeSubstanceId', 'IN', drugItemIds), cmp('prescribedNameId', 'IN', drugItemIds)))
            .where('therapyDate', '>=', now)
            .whereExists('therapySequence', sequence => sequence.where('status', 'IN', [
            PatientTherapySequenceStatus.enum.pending_doctor_validation,
            PatientTherapySequenceStatus.enum.pending_approval,
        ]))
            .where('deletedAt', 'IS', null));
        if (medicines.length === 0)
            return;
        const perDrugReduction = new Map();
        for (const dr of doseReductions) {
            perDrugReduction.set(dr.drugCatalogItemId, dr.doseReduction);
        }
        await Promise.all(medicines.map(async (med) => {
            if (!med.regularDose) {
                return;
            }
            // The dialog lists trade-name-only medicines under their prescribed name id.
            const drugReduction = (med.activeSubstanceId ? perDrugReduction.get(med.activeSubstanceId) : undefined) ??
                (med.prescribedNameId ? perDrugReduction.get(med.prescribedNameId) : undefined);
            if (drugReduction === undefined) {
                return;
            }
            await tx.mutate.patientTherapySequenceMedicine.update({
                id: med.id,
                doseProportion: new Decimal(100).minus(drugReduction).toNumber(),
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
        }));
        await medicineUpdateTotalAmounts(tx, patientId);
    }),
    addGfr: defineMutator(AddPatientGfrSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'patients.edit');
        // the saved GFR drives AUC doses, so an implausible one is refused, not only labelled (CY-348)
        if (!isGfrPlausible(input.gfr)) {
            throw new Error(`GFR ${input.gfr} mL/min is outside the plausible range (above 0, at most ${GFR_PLAUSIBLE_MAX}). Check the inputs.`);
        }
        const prev = await findById(tx, input.patientId);
        if (!prev)
            throw new Error('Not Found');
        const now = Date.now();
        const characteristics = await tx.run(zql.patientCharacteristics.where('patientId', '=', input.patientId).orderBy('createdAt', 'desc').limit(1).one());
        const gfrRecord = {
            id: uuidv7(),
            patientId: input.patientId,
            labTime: input.labTime,
            gfr: input.gfr,
            method: input.method,
            serumCreatinine: input.serumCreatinine,
            serumUnit: input.serumUnit,
            urineCreatinine: input.urineCreatinine,
            urineVolume: input.urineVolume,
            collectionTime: input.collectionTime,
            ageMonths: isDefined(prev.birthDate) ? getAgeInMonths(prev.birthDate, now) : null,
            weight: characteristics?.weight ?? null,
            height: characteristics?.height ?? null,
            bsa: characteristics?.bsa ?? null,
            createdAt: now,
            createdBy: ctx.user.id,
        };
        const gfrInUseBefore = await tx.run(inUseGfrQuery(input.patientId));
        await tx.mutate.patientGfrs.insert(gfrRecord);
        await medicineUpdateTotalAmounts(tx, input.patientId, [], {
            cause: 'gfr_added',
            userId: ctx.user.id,
            impersonatedBy: ctx.impersonatedBy,
        });
        if (tx.location === 'server') {
            const gfrInUseAfter = await tx.run(inUseGfrQuery(input.patientId));
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientGfr,
                resourceId: gfrRecord.id,
                action: 'create',
                domain: 'patient',
                event: EventLogs.PATIENT_GFR_ADDED,
                patches: createPatch({}, pickLoggedGfr(gfrRecord)),
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    code: prev.identifiers[0]?.value,
                    name: prev.fullName,
                    gfr: String(gfrRecord.gfr),
                    oldGfrInUse: formatLoggedNumber(gfrInUseBefore?.gfr),
                    newGfrInUse: formatLoggedNumber(gfrInUseAfter?.gfr),
                },
            }, ctx);
        }
    }),
    deleteGfr: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'patients.edit');
        const gfr = await tx.run(zql.patientGfrs.where('id', args.id).where('deletedAt', 'IS', null).one());
        if (!gfr)
            throw new Error('Not Found');
        const patient = await findById(tx, gfr.patientId);
        if (!patient)
            throw new Error('Not Found');
        const now = Date.now();
        const deleted = { id: gfr.id, deletedAt: now, deletedBy: ctx.user.id };
        const gfrInUseBefore = await tx.run(inUseGfrQuery(gfr.patientId));
        await tx.mutate.patientGfrs.update(deleted);
        // Deleting the GFR in use changes the value AUC doses are calculated from.
        await medicineUpdateTotalAmounts(tx, gfr.patientId, [], {
            cause: 'gfr_deleted',
            userId: ctx.user.id,
            impersonatedBy: ctx.impersonatedBy,
        });
        if (tx.location === 'server') {
            const gfrInUseAfter = await tx.run(inUseGfrQuery(gfr.patientId));
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientGfr,
                resourceId: gfr.id,
                action: 'delete',
                domain: 'patient',
                event: EventLogs.PATIENT_GFR_DELETED,
                patches: [
                    { op: 'test', path: '/gfr', value: gfr.gfr },
                    { op: 'add', path: '/deletedAt', value: now },
                    { op: 'add', path: '/deletedBy', value: ctx.user.id },
                ],
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'WARNING',
                details: {
                    code: patient.identifiers[0]?.value,
                    name: patient.fullName,
                    gfr: String(gfr.gfr),
                    method: gfr.method,
                    labTime: new Date(gfr.labTime).toISOString(),
                    oldGfrInUse: formatLoggedNumber(gfrInUseBefore?.gfr),
                    newGfrInUse: formatLoggedNumber(gfrInUseAfter?.gfr),
                },
            }, ctx);
        }
    }),
    importFromHl7: defineMutator(ImportPatientFromHl7Schema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'patients.edit');
        const patient = await findById(tx, input.patientId);
        if (!patient)
            throw new Error('Patient not found');
        if (patient.source !== PatientSource.enum.hl7_message)
            throw new Error('Patient was not created from HL7 message');
        const userActions = {
            updatedAt: Date.now(),
            updatedBy: ctx.user.id,
        };
        const updatedPatient = {
            id: patient.id,
            source: PatientSource.enum.hl7_import,
            searchText: computePatientSearchText({
                givenName: patient.givenName,
                familyName: patient.familyName,
                hospitalPatientId: patient.hospitalPatientId,
                nationalId: patient.nationalId,
            }),
            ...userActions,
        };
        await tx.mutate.patients.update(updatedPatient);
        if (tx.location === 'server') {
            const patches = createPatch(patient, updatedPatient);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'patient',
                resourceId: updatedPatient.id,
                action: 'update',
                domain: 'patient',
                event: EventLogs.PATIENT_IMPORTED,
                patches,
                performedBy: userActions.updatedBy,
                performedAt: userActions.updatedAt,
                status: 'OK',
                details: { name: patient.fullName, code: patient.identifiers[0]?.value },
            }, ctx);
        }
    }),
    mergePatients: defineMutator(MergePatientsSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'patients.edit');
        const { sourceId, targetId } = input;
        const sourcePatient = await findById(tx, sourceId);
        const targetPatient = await findById(tx, targetId); // the one from who to use the demographic data and copy it to the source patient
        if (!sourcePatient || !targetPatient) {
            throw new Error('Source or target patient not found');
        }
        const now = Date.now();
        const actor = ctx.user.id;
        // the target is kept (with the source's name), the source is merged into it
        const mergeDetails = {
            name: sourcePatient.fullName,
            code: targetPatient.identifiers[0]?.value,
            mergedName: sourcePatient.fullName,
            mergedCode: sourcePatient.identifiers[0]?.value,
        };
        // should be the local record (patient.source = manual, hl7_import)
        async function updateTargetPatient() {
            if (!sourcePatient || !targetPatient)
                return;
            // move the source's contacts to the target patient
            const sourceContacts = await tx.run(zql.patientContacts.where('patientId', '=', sourceId).where('deletedAt', 'IS', null));
            const targetInsurance = await tx.run(zql.patientInsurances.where('patientId', '=', targetId).one());
            const updatedPatient = {
                id: targetId,
                givenName: sourcePatient.givenName,
                familyName: sourcePatient.familyName,
                sortableGivenName: toSortableField(sourcePatient.givenName),
                sortableFamilyName: toSortableField(sourcePatient.familyName),
                fullName: sourcePatient.fullName,
                searchText: computePatientSearchText({
                    givenName: sourcePatient.givenName,
                    familyName: sourcePatient.familyName,
                    hospitalPatientId: targetPatient.hospitalPatientId,
                    nationalId: targetPatient.nationalId,
                    insuranceNumber: targetInsurance?.insuranceNumber,
                }),
                birthDate: sourcePatient.birthDate,
                updatedAt: now,
                updatedBy: actor,
            };
            const promises = [
                tx.mutate.patients.update(updatedPatient),
                ...sourceContacts.map(c => tx.mutate.patientContacts.update({
                    id: c.id,
                    patientId: updatedPatient.id,
                    updatedAt: now,
                    updatedBy: actor,
                })),
            ];
            if (tx.location === 'server') {
                const patches = createPatch(targetPatient, updatedPatient);
                promises.push(addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'patient',
                    resourceId: updatedPatient.id,
                    action: 'update',
                    domain: 'patient',
                    event: EventLogs.PATIENT_MERGED,
                    patches,
                    performedBy: actor,
                    performedAt: now,
                    status: 'OK',
                    details: mergeDetails,
                }, ctx), ...sourceContacts.map(c => {
                    const contactPatches = createPatch(c, {
                        ...c,
                        patientId: updatedPatient.id,
                        updatedAt: now,
                        updatedBy: actor,
                    });
                    return addEventLog(tx, {
                        id: uuidv7(),
                        resource: 'patient_contact',
                        resourceId: c.id,
                        action: 'update',
                        domain: 'patient',
                        event: EventLogs.PATIENT_MERGED,
                        patches: contactPatches,
                        performedBy: actor,
                        performedAt: now,
                        status: 'OK',
                        details: mergeDetails,
                    }, ctx);
                }));
            }
            return await Promise.all(promises);
        }
        // should be the hl7 record
        async function updateSourcePatient() {
            const updatedPatient = {
                id: sourceId,
                deletedAt: now,
                deletedBy: actor,
            };
            const promises = [tx.mutate.patients.update(updatedPatient)];
            if (tx.location === 'server') {
                const patches = createPatch(sourcePatient, updatedPatient);
                promises.push(addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'patient',
                    resourceId: updatedPatient.id,
                    action: 'delete',
                    domain: 'patient',
                    event: EventLogs.PATIENT_MERGED,
                    patches,
                    performedBy: actor,
                    performedAt: now,
                    status: 'OK',
                    details: mergeDetails,
                }, ctx));
            }
            return await Promise.all(promises);
        }
        await updateTargetPatient();
        await updateSourcePatient();
    }),
    recordPatientView: defineMutator(z.uuidv7(), async ({ args, ctx, tx }) => {
        assertIsAuthenticated(ctx);
        if (tx.location === 'server') {
            const now = Date.now();
            const actor = ctx.user.id;
            const patient = await findById(tx, args);
            if (!patient)
                return;
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'patient',
                resourceId: args,
                action: 'access',
                domain: 'patient',
                event: EventLogs.PATIENT_ACCESSED,
                patches: [],
                performedBy: actor,
                performedAt: now,
                status: 'OK',
                details: {
                    name: patient?.fullName,
                },
            }, ctx);
        }
    }),
    logTherapyPrint: defineMutator(z.object({ patientId: z.uuidv7(), therapySequenceIds: z.array(z.uuidv7()).min(1) }), async ({ args, ctx, tx }) => {
        assertIsAuthenticated(ctx);
        if (tx.location !== 'server')
            return;
        const patient = await findById(tx, args.patientId);
        if (!patient)
            return;
        await addEventLog(tx, {
            id: uuidv7(),
            resource: 'patient',
            resourceId: args.patientId,
            action: 'access',
            domain: 'patient',
            event: EventLogs.PRESCRIPTIONS_PRINTED,
            patches: [],
            performedBy: ctx.user.id,
            performedAt: Date.now(),
            status: 'OK',
            details: {
                name: patient.fullName,
                count: String(args.therapySequenceIds.length),
            },
        }, ctx);
    }),
};
//# sourceMappingURL=patient.mutators.js.map